/**
 * Org / account client helpers (Profile, Stock).
 */
import { ENGINE_BASE, engineFetch } from "./config";

function baseUrl(): string {
  return (
    (typeof window !== "undefined" && (window as { __ENGINE_URL__?: string }).__ENGINE_URL__) ||
    ENGINE_BASE
  );
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await engineFetch(`${baseUrl()}${path}`, init);
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    const msg =
      typeof detail === "object" &&
      detail &&
      "detail" in detail &&
      typeof (detail as { detail: unknown }).detail === "string"
        ? (detail as { detail: string }).detail
        : JSON.stringify(detail);
    throw new Error(`${path} ${res.status}: ${msg}`);
  }
  return res.json() as Promise<T>;
}

export type AuthMeResponse = {
  tenant?: string;
  subject?: string;
  user_id?: string | null;
  organization_id?: string | null;
  organization_slug?: string | null;
  email?: string | null;
  display_name?: string | null;
  auth_via?: string;
  anonymous?: boolean;
  role?: string | null;
  can_write?: boolean;
};

export type StockSummaryResponse = {
  tenant?: string;
  source?: string;
  item_count?: number;
  global_fallback_allowed?: boolean;
};

export type StockImportResponse = {
  imported?: number;
  organization_slug?: string;
  source?: string;
  note?: string;
};

export type ProfileUpdateResponse = {
  ok?: boolean;
  user_id?: string;
  email?: string;
  display_name?: string | null;
};

export function fetchAuthMe(): Promise<AuthMeResponse> {
  return jsonFetch<AuthMeResponse>("/v1/auth/me");
}

export type McpInstallMeta = {
  id: string;
  label: string;
  created_at?: string | null;
  last_used_at?: string | null;
};

export type McpInstallCreated = {
  id: string;
  token: string;
  label?: string;
  cursor_config: Record<string, unknown>;
  claude_config: Record<string, unknown>;
};

const LOCAL_MCP_ENGINE = "http://127.0.0.1:9472";

function isAbsoluteHttp(value: string): boolean {
  return value.indexOf("http://") === 0 || value.indexOf("https://") === 0;
}

/**
 * Absolute engine URL for a laptop MCP process.
 * A baked http(s) base wins. An empty base is the browser's own origin when
 * that page is not this machine (dev.app), and the local engine otherwise.
 * The Vite dev proxy is not an address the laptop process can call.
 */
export function mcpEngineUrl(
  page?: { hostname?: string; origin?: string } | null,
  base: string = ENGINE_BASE,
): string {
  const trimmed = (base || "").trim();
  if (isAbsoluteHttp(trimmed)) {
    return trimmed.replace(/\/+$/, "");
  }
  const where =
    page !== undefined ? page : typeof window !== "undefined" ? window.location : null;
  const host = (where?.hostname || "").toLowerCase();
  const local =
    host === "" ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "[::1]";
  const origin = (where?.origin || "").replace(/\/+$/, "");
  if (!local && isAbsoluteHttp(origin)) return origin;
  return LOCAL_MCP_ENGINE;
}

/** The running engine has no MCP install routes (404 or 405). */
export class McpInstallsUnavailable extends Error {
  readonly status: 404 | 405;
  constructor(status: 404 | 405) {
    super("mcp_installs_unavailable");
    this.name = "McpInstallsUnavailable";
    this.status = status;
  }
}

/**
 * MCP install calls never put the response body on the error.
 * A missing route is `McpInstallsUnavailable`; anything else is a fixed code.
 */
async function mcpFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await engineFetch(`${baseUrl()}${path}`, init);
  if (res.status === 404 || res.status === 405) throw new McpInstallsUnavailable(res.status);
  if (!res.ok) throw new Error("mcp_installs_failed");
  if (res.status === 204) return {} as T;
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error("mcp_installs_failed");
  }
}

export function listMcpInstalls(): Promise<{ items: McpInstallMeta[]; count?: number }> {
  return mcpFetch<{ items: McpInstallMeta[]; count?: number }>("/v1/auth/mcp-installs");
}

export function createMcpInstall(body: {
  label?: string;
  engine_url: string;
}): Promise<McpInstallCreated> {
  return mcpFetch<McpInstallCreated>("/v1/auth/mcp-installs", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function revokeMcpInstall(id: string): Promise<{ ok?: boolean }> {
  return mcpFetch<{ ok?: boolean }>(`/v1/auth/mcp-installs/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function updateProfile(displayName: string): Promise<ProfileUpdateResponse> {
  return jsonFetch<ProfileUpdateResponse>("/v1/auth/me", {
    method: "PATCH",
    body: JSON.stringify({ display_name: displayName }),
  });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<{ ok?: boolean }> {
  return jsonFetch<{ ok?: boolean }>("/v1/auth/password", {
    method: "POST",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

export function fetchStockSummary(): Promise<StockSummaryResponse> {
  return jsonFetch<StockSummaryResponse>("/v1/stock");
}

/** Replace org stock catalog from CSV (owner/member/service). */
export async function importStockCsv(file: File): Promise<StockImportResponse> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await engineFetch(`${baseUrl()}/v1/stock/import`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    throw new Error(`stock import ${res.status}: ${JSON.stringify(detail)}`);
  }
  return res.json() as Promise<StockImportResponse>;
}
